import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { mapDisputeTypeApiToUi, mapWorkflowApiToUi, mapDisputeCategoryApiToOption, type DisputeCategoryOption } from "@/lib/mappers/dispute-types";
import { generateMockDisputeTypes } from "@/lib/mock-data/dispute-types-mock-data";
import type { DisputeType, WorkflowStage } from "@/types/dispute-types";
import type {
  DisputeTypeApiResponse,
  DisputeCategoryApiResponse,
  CreateDisputeTypeRequest,
  UpdateDisputeTypeRequest,
  UpdateDisputeTypeResponse,
  WorkflowApiResponse,
  WorkflowStageApiResponse,
} from "@/types/api/dispute-types";

// Use a mutable reference for mock data so mutations can update it
let mockDisputeTypes = generateMockDisputeTypes();

const mockCategories: DisputeCategoryOption[] = [
  { id: 1, code: "billing", name: "Billing" },
  { id: 2, code: "service", name: "Service" },
  { id: 3, code: "payment", name: "Payment" },
  { id: 4, code: "contract", name: "Contract" },
  { id: 5, code: "other", name: "Other" },
];

export function useDisputeTypesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeType[], Error>(
    ["dispute-types-settings"],
    async ({ signal }) => {
      const [disputeTypesResponse, workflowsResponse] = await Promise.all([
        apiGet<DisputeTypeApiResponse[]>("/api/v1/settings/dispute-types", undefined, signal),
        apiGet<WorkflowApiResponse[]>("/api/v1/settings/workflows", undefined, signal),
      ]);

      const workflowStageCountByDisputeTypeId = new Map<number, number>(
        workflowsResponse.map(x => [x.disputeTypeId, x.stageCount])
      );

      return disputeTypesResponse.map(x =>
        mapDisputeTypeApiToUi(x, undefined, workflowStageCountByDisputeTypeId.get(x.id))
      );
    },
    mockDisputeTypes,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputeTypeById(disputeTypeId: number | string | null) {
  const { apiGet } = useApi();
  const mockDisputeType = mockDisputeTypes.find(x => x.id === String(disputeTypeId)) ?? null;

  return useModeAwareQuery<DisputeType | null, Error>(
    ["dispute-type", disputeTypeId],
    async ({ signal }) => {
      if (!disputeTypeId) return null;
      const response = await apiGet<DisputeTypeApiResponse>(`/api/v1/settings/dispute-types/${disputeTypeId}`, undefined, signal);

      // Fetch the workflow and its stages for this dispute type
      let workflow;
      try {
        const workflows = await apiGet<WorkflowApiResponse[]>('/api/v1/settings/workflows', undefined, signal);
        const matchingWorkflow = workflows.find(w => w.disputeTypeId === response.id);

        if (matchingWorkflow) {
          const stages = await apiGet<WorkflowStageApiResponse[]>(
            `/api/v1/settings/workflows/${matchingWorkflow.id}/stages`, undefined, signal
          );
          workflow = mapWorkflowApiToUi(matchingWorkflow, stages);
        }
      } catch (e) {
        console.warn('Failed to fetch workflow data for dispute type:', e);
      }

      return mapDisputeTypeApiToUi(response, workflow);
    },
    mockDisputeType,
    {
      enabled: !!disputeTypeId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputeCategories() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeCategoryOption[], Error>(
    ["dispute-categories"],
    async ({ signal }) => {
      const response = await apiGet<DisputeCategoryApiResponse[]>("/api/v1/settings/dispute-types/categories", undefined, signal);
      return response.map(x => mapDisputeCategoryApiToOption(x));
    },
    mockCategories,
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}

export function useCreateDisputeType() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<DisputeType, Error, DisputeType>(
    async (data) => {
      // Step 1: Create dispute type
      const createRequest: CreateDisputeTypeRequest = {
        name: data.name,
        description: data.description || undefined,
        isActive: data.isActive,
      };
      const disputeTypeResponse = await apiPost<DisputeTypeApiResponse>("/api/v1/settings/dispute-types", createRequest);

      // Step 2: Create workflow
      const workflowResponse = await apiPost<{ id: number }>("/api/v1/settings/workflows", {
        disputeTypeId: disputeTypeResponse.id,
        name: data.name,
        description: data.description || undefined,
      });

      // Step 3: Create each stage
      for (const stage of data.workflow.stages) {
        await apiPost(`/api/v1/settings/workflows/${workflowResponse.id}/stages`, {
          name: stage.name,
          description: stage.description,
          sortOrder: stage.order,
          color: stage.color,
          icon: null,
          properties: JSON.stringify(stage.properties),
          notifications: stage.notifications?.length ? JSON.stringify(stage.notifications) : null,
          requiredFields: null,
          allowedActions: null,
        });
      }

      // Return the mapped dispute type
      return mapDisputeTypeApiToUi(disputeTypeResponse);
    },
    async (disputeType) => {
      // Create a new dispute type with a unique ID for mock data
      const newDisputeType: DisputeType = {
        ...disputeType,
        id: `dt-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'Current User',
        usageCount: 0,
        avgResolutionTime: 0,
        successRate: 0,
        isActive: false,
        workflow: {
          ...disputeType.workflow,
          id: `wf-${Date.now()}`,
          disputeTypeId: `dt-${Date.now()}`,
        },
      };
      
      // Add the new dispute type to mock data
      mockDisputeTypes = [...mockDisputeTypes, newDisputeType];
      return newDisputeType;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
      },
    }
  );
}

export function useUpdateDisputeType(disputeTypeId: number | string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateDisputeTypeResponse, Error, UpdateDisputeTypeRequest>(
    async (data) => {
      return apiPut<UpdateDisputeTypeResponse>(`/api/v1/settings/dispute-types/${disputeTypeId}`, data);
    },
    async (data) => {
      return {
        id: Number(disputeTypeId),
        name: data.name ?? "Updated Dispute Type",
        description: data.description,
        categoryId: data.categoryId,
        categoryName: undefined,
        isActive: data.isActive ?? true,
        usageCount: 0,
        avgResolutionTime: undefined,
        successRate: undefined,
        createdByUserId: undefined,
        createdByUserName: "Current User",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", disputeTypeId] });
      },
    }
  );
}

export function useDeleteDisputeType() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, number | string>(
    async (disputeTypeId) => {
      await apiDelete(`/api/v1/settings/dispute-types/${disputeTypeId}`);
    },
    async (disputeTypeId) => {
      // Remove from mock data
      const index = mockDisputeTypes.findIndex(dt => dt.id === String(disputeTypeId));
      if (index !== -1) {
        mockDisputeTypes.splice(index, 1);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
      },
    }
  );
}

export function useToggleDisputeTypeStatus() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateDisputeTypeResponse, Error, { id: number | string; isActive: boolean }>(
    async (data) => {
      return apiPut<UpdateDisputeTypeResponse>(`/api/v1/settings/dispute-types/${data.id}`, { isActive: data.isActive });
    },
    async (data) => {
      // Update the mock data
      const disputeType = mockDisputeTypes.find(dt => dt.id === String(data.id));
      if (disputeType) {
        disputeType.isActive = data.isActive;
        disputeType.updatedAt = new Date().toISOString();
      }
      
      return {
        id: Number(data.id),
        name: disputeType?.name || "Dispute Type",
        description: disputeType?.description,
        categoryId: undefined,
        categoryName: undefined,
        isActive: data.isActive,
        usageCount: disputeType?.usageCount || 0,
        avgResolutionTime: disputeType?.avgResolutionTime,
        successRate: disputeType?.successRate,
        createdByUserId: undefined,
        createdByUserName: disputeType?.createdBy || "Current User",
        createdAt: disputeType?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", variables.id] });
      },
    }
  );
}

interface UpdateWorkflowPayload {
  workflowName: string;
  description: string;
  levels: {
    id: string;
    levelNumber: number;
    description: string;
    followUpDays: number;
    escalationDays: number;
    assignedUser: string;
    escalationUser: string;
    documentTemplate: string;
    sendCorrespondence: boolean;
  }[];
  resolvedStage?: WorkflowStage;
}

export function useDuplicateDisputeType() {
  const { apiGet, apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<DisputeType, Error, { sourceId: number | string; newName: string }>(
    async ({ sourceId, newName }) => {
      // Step 1: Fetch the source dispute type
      const sourceDisputeType = await apiGet<DisputeTypeApiResponse>(
        `/api/v1/settings/dispute-types/${sourceId}`
      );

      // Step 2: Fetch the source workflow and its stages
      const workflows = await apiGet<WorkflowApiResponse[]>('/api/v1/settings/workflows');
      const sourceWorkflow = workflows.find(x => x.disputeTypeId === sourceDisputeType.id);

      let sourceStages: WorkflowStageApiResponse[] = [];
      if (sourceWorkflow) {
        sourceStages = await apiGet<WorkflowStageApiResponse[]>(
          `/api/v1/settings/workflows/${sourceWorkflow.id}/stages`
        );
      }

      // Step 3: Create the new dispute type
      const newDisputeType = await apiPost<DisputeTypeApiResponse>(
        '/api/v1/settings/dispute-types',
        {
          name: newName,
          description: sourceDisputeType.description || undefined,
          isActive: false,
        }
      );

      // Step 4: Create a new workflow for the duplicate
      const newWorkflow = await apiPost<{ id: number }>(
        '/api/v1/settings/workflows',
        {
          disputeTypeId: newDisputeType.id,
          name: newName,
          description: sourceDisputeType.description || undefined,
        }
      );

      // Step 5: Recreate each stage from the source
      for (const stage of sourceStages.sort((a, b) => a.sortOrder - b.sortOrder)) {
        await apiPost(`/api/v1/settings/workflows/${newWorkflow.id}/stages`, {
          name: stage.name,
          description: stage.description,
          sortOrder: stage.sortOrder,
          color: stage.color,
          icon: stage.icon,
          properties: stage.properties,
          notifications: stage.notifications,
          requiredFields: stage.requiredFields,
          allowedActions: stage.allowedActions,
        });
      }

      return mapDisputeTypeApiToUi(newDisputeType);
    },
    async ({ newName }) => {
      // Demo mode: create a mock duplicate
      const newDisputeType: DisputeType = {
        id: `dt-${Date.now()}`,
        name: newName,
        description: '',
        isActive: false,
        isSystemType: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'Current User',
        usageCount: 0,
        avgResolutionTime: 0,
        successRate: 0,
        workflow: {
          id: `wf-${Date.now()}`,
          disputeTypeId: `dt-${Date.now()}`,
          stages: [],
          transitions: [],
        },
      };
      mockDisputeTypes = [...mockDisputeTypes, newDisputeType];
      return newDisputeType;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
      },
    }
  );
}

export function useActiveDisputeCount(disputeTypeId: number | string | null) {
  const { apiGet } = useApi();

  return useModeAwareQuery<number, Error>(
    ["active-dispute-count", disputeTypeId],
    async ({ signal }) => {
      if (!disputeTypeId) return 0;
      const response = await apiGet<{ count: number }>(
        `/api/v1/settings/dispute-types/${disputeTypeId}/active-dispute-count`,
        undefined,
        signal
      );
      return response.count;
    },
    0,
    {
      enabled: !!disputeTypeId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

function haveWorkflowStagesChanged(
  existingStages: WorkflowStageApiResponse[],
  newLevels: UpdateWorkflowPayload['levels']
): boolean {
  // Filter out the system "Resolved" stage for comparison
  const editableStages = existingStages
    .filter(x => x.name !== 'Resolved')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (editableStages.length !== newLevels.length) {
    return true;
  }

  return editableStages.some((stage, index) => {
    const level = newLevels[index];
    const existingProps = stage.properties ? JSON.parse(stage.properties) : {};

    return (
      stage.description !== level.description ||
      stage.sortOrder !== level.levelNumber ||
      existingProps.followUpDays !== level.followUpDays ||
      existingProps.escalationDays !== level.escalationDays ||
      existingProps.allowedAssigneeRoles?.[0] !== level.assignedUser ||
      existingProps.escalationUser !== level.escalationUser ||
      existingProps.documentTemplate !== level.documentTemplate ||
      existingProps.sendCorrespondence !== level.sendCorrespondence
    );
  });
}

export function useUpdateDisputeTypeWorkflow(disputeTypeId: string) {
  const { apiGet, apiPost, apiPut, apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, UpdateWorkflowPayload>(
    async (workflowData) => {
      // Get the workflow ID for this dispute type
      const workflows = await apiGet<WorkflowApiResponse[]>('/api/v1/settings/workflows');
      const matchingWorkflow = workflows.find(x => x.disputeTypeId === Number(disputeTypeId));

      if (!matchingWorkflow) {
        throw new Error('No workflow found for this dispute type');
      }

      const workflowId = matchingWorkflow.id;

      // Get existing stages
      const existingStages = await apiGet<WorkflowStageApiResponse[]>(
        `/api/v1/settings/workflows/${workflowId}/stages`
      );

      // Only delete/recreate stages and update the workflow if stages actually changed
      const stagesChanged = haveWorkflowStagesChanged(existingStages, workflowData.levels);

      if (stagesChanged) {
        // Delete all existing stages
        for (const stage of existingStages) {
          await apiDelete(`/api/v1/settings/workflows/${workflowId}/stages/${stage.id}`);
        }

        // Create new stages from the levels
        for (const level of workflowData.levels) {
          await apiPost(`/api/v1/settings/workflows/${workflowId}/stages`, {
            name: `Level ${level.levelNumber}`,
            description: level.description,
            sortOrder: level.levelNumber,
            color: null,
            icon: null,
            properties: JSON.stringify({
              slaHours: level.escalationDays * 24,
              slaWarningHours: level.followUpDays * 24,
              followUpDays: level.followUpDays,
              escalationDays: level.escalationDays,
              allowedAssigneeRoles: [level.assignedUser],
              escalationUser: level.escalationUser,
              documentTemplate: level.documentTemplate,
              sendCorrespondence: level.sendCorrespondence,
              requiresApproval: false,
              autoEscalate: true,
              allowReassignment: false,
            }),
            notifications: null,
            requiredFields: null,
            allowedActions: null,
          });
        }

        // Re-create the resolved stage
        await apiPost(`/api/v1/settings/workflows/${workflowId}/stages`, {
          name: 'Resolved',
          description: 'Dispute has been resolved',
          sortOrder: workflowData.levels.length + 1,
          color: '#10b981',
          icon: null,
          properties: JSON.stringify({
            slaHours: 0,
            slaWarningHours: 0,
            sendCorrespondence: true,
            requiresApproval: false,
            autoEscalate: false,
            allowReassignment: false,
          }),
          notifications: null,
          requiredFields: null,
          allowedActions: null,
        });

        // Update the workflow name/description (triggers WorkflowChanged audit log)
        await apiPut(`/api/v1/settings/workflows/${workflowId}`, {
          name: workflowData.workflowName,
          description: workflowData.description,
          status: 'active',
        });
      }

      // Always update the dispute type record name/description
      await apiPut(`/api/v1/settings/dispute-types/${disputeTypeId}`, {
        name: workflowData.workflowName,
        description: workflowData.description,
      });
    },
    async (workflowData) => {
      // Update the mock data
      const disputeType = mockDisputeTypes.find(dt => dt.id === String(disputeTypeId));
      if (disputeType?.workflow) {
        // Rebuild stages from the workflow levels
        const newStages: WorkflowStage[] = workflowData.levels.map((level, index) => ({
          id: level.id || `stage-${Date.now()}-${index}`,
          name: `Level ${index + 1}`,
          description: level.description || `Escalation level ${index + 1}`,
          order: index + 1,
          color: ['#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981'][index] || '#8b5cf6',
          properties: {
            slaHours: level.escalationDays * 24,
            slaWarningHours: level.followUpDays * 24,
            followUpDays: level.followUpDays,
            escalationDays: level.escalationDays,
            allowedAssigneeRoles: [level.assignedUser],
            escalationUser: level.escalationUser,
            documentTemplate: level.documentTemplate,
            sendCorrespondence: level.sendCorrespondence,
            requiresApproval: false,
            autoEscalate: true,
            allowReassignment: false,
          },
          notifications: [],
        }));

        // Re-append the resolved stage if it was passed through
        if (workflowData.resolvedStage) {
          newStages.push({
            ...workflowData.resolvedStage,
            order: newStages.length + 1,
          });
        } else {
          // Create a default resolved stage
          newStages.push({
            id: `stage-resolved-${Date.now()}`,
            name: 'Resolved',
            description: 'Dispute has been resolved',
            order: newStages.length + 1,
            color: '#10b981',
            properties: {
              slaHours: 0,
              slaWarningHours: 0,
              sendCorrespondence: true,
              requiresApproval: false,
              autoEscalate: false,
              allowReassignment: false,
            },
            notifications: [],
          });
        }

        disputeType.workflow.stages = newStages;
        disputeType.name = workflowData.workflowName || disputeType.name;
        disputeType.description = workflowData.description || disputeType.description;
        disputeType.updatedAt = new Date().toISOString();
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types-settings"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", disputeTypeId] });
      },
    }
  );
}
